import type { ViewState, SortField, SortOrder } from './types';

/**
 * Format an ISO date string to a human-readable format.
 */
export function formatDate(isoDate: string): string {
  if (!isoDate) return 'N/A';

  try {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoDate;
  }
}

/**
 * All known environments with their color configurations.
 * Used by Help page to display the color key.
 */
export const ENVIRONMENT_COLORS: Array<{
  name: string;
  bgColor: string;
  textColor: string;
}> = [
  { name: 'QA', bgColor: '#e3f2fd', textColor: '#1565c0' },
  { name: 'UA', bgColor: '#e8f5e9', textColor: '#2e7d32' },
  { name: 'PROD', bgColor: '#ffebee', textColor: '#c62828' },
  { name: 'DEV', bgColor: '#fffde7', textColor: '#f9a825' },
  { name: 'SYMBOLIC', bgColor: '#e3f2fd', textColor: '#1565c0' },
  { name: 'EVERGREENS', bgColor: '#e3f2fd', textColor: '#1565c0' },
  { name: 'NEXUS', bgColor: '#e3f2fd', textColor: '#1565c0' },
];

export const DEFAULT_ENV_COLOR = { bgColor: '#F0F6F8', textColor: '#182023' };

/**
 * Get background color for an environment.
 */
export function getEnvColor(env: string): string {
  const found = ENVIRONMENT_COLORS.find(e => e.name === env.toUpperCase());
  return found ? found.bgColor : DEFAULT_ENV_COLOR.bgColor;
}

/**
 * Get text color for better contrast on environment backgrounds.
 */
export function getEnvTextColor(env: string): string {
  const found = ENVIRONMENT_COLORS.find(e => e.name === env.toUpperCase());
  return found ? found.textColor : DEFAULT_ENV_COLOR.textColor;
}

/**
 * Truncate a string to a maximum length.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 3) + '...';
}

/**
 * Generate a unique ID.
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15);
}

// ============================================
// View State Persistence (URL params + cookies)
// ============================================

const COOKIE_NAME = 'envappver_view';
const FAVORITES_COOKIE_NAME = 'envappver_favorites';
const COOKIE_EXPIRY_DAYS = 30;

const DEFAULT_VIEW_STATE: ViewState = {
  filters: { env: '', app: '' },
  sort: { field: 'DeployAt', order: 'desc' },
  showDummy: false,
  showFavoritesOnly: false,
};

const VALID_SORT_FIELDS: SortField[] = ['Account', 'Env', 'App', 'Ver', 'DeployAt', 'DeployFor'];
const VALID_SORT_ORDERS: SortOrder[] = ['asc', 'desc'];

/**
 * Parse URL query parameters into partial view state.
 */
export function parseUrlParams(): Partial<ViewState> {
  const params = new URLSearchParams(window.location.search);
  const result: Partial<ViewState> = {};

  const env = params.get('env');
  const app = params.get('app');
  if (env !== null || app !== null) {
    result.filters = {
      env: env || '',
      app: app || '',
    };
  }

  const sort = params.get('sort') as SortField | null;
  const order = params.get('order') as SortOrder | null;
  if (sort && VALID_SORT_FIELDS.includes(sort)) {
    result.sort = {
      field: sort,
      order: order && VALID_SORT_ORDERS.includes(order) ? order : 'asc',
    };
  }

  const dummy = params.get('dummy');
  if (dummy !== null) {
    result.showDummy = dummy === 'true';
  }

  const favOnly = params.get('favonly');
  if (favOnly !== null) {
    result.showFavoritesOnly = favOnly === 'true';
  }

  return result;
}

/**
 * Build URL query string from view state (only non-default values).
 */
export function buildUrlParams(state: ViewState): string {
  const params = new URLSearchParams();

  if (state.filters.env) {
    params.set('env', state.filters.env);
  }
  if (state.filters.app) {
    params.set('app', state.filters.app);
  }
  if (state.sort.field !== DEFAULT_VIEW_STATE.sort.field) {
    params.set('sort', state.sort.field);
    params.set('order', state.sort.order);
  } else if (state.sort.order !== DEFAULT_VIEW_STATE.sort.order) {
    params.set('order', state.sort.order);
  }
  if (state.showDummy !== DEFAULT_VIEW_STATE.showDummy) {
    params.set('dummy', String(state.showDummy));
  }
  if (state.showFavoritesOnly !== DEFAULT_VIEW_STATE.showFavoritesOnly) {
    params.set('favonly', String(state.showFavoritesOnly));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Load view state from cookie.
 */
export function loadFromCookie(): Partial<ViewState> {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === COOKIE_NAME && value) {
        const parsed = JSON.parse(decodeURIComponent(value));
        const result: Partial<ViewState> = {};

        if (parsed.env !== undefined || parsed.app !== undefined) {
          result.filters = {
            env: parsed.env || '',
            app: parsed.app || '',
          };
        }
        if (parsed.sort && VALID_SORT_FIELDS.includes(parsed.sort)) {
          result.sort = {
            field: parsed.sort,
            order: VALID_SORT_ORDERS.includes(parsed.order) ? parsed.order : 'asc',
          };
        }
        if (parsed.dummy !== undefined) {
          result.showDummy = Boolean(parsed.dummy);
        }
        if (parsed.favonly !== undefined) {
          result.showFavoritesOnly = Boolean(parsed.favonly);
        }

        return result;
      }
    }
  } catch {
    // Invalid cookie, ignore
  }
  return {};
}

/**
 * Save view state to cookie.
 */
export function saveToCookie(state: ViewState): void {
  const data = {
    env: state.filters.env,
    app: state.filters.app,
    sort: state.sort.field,
    order: state.sort.order,
    dummy: state.showDummy,
    favonly: state.showFavoritesOnly,
  };

  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))};expires=${expires.toUTCString()};path=/`;
}

/**
 * Get initial view state by merging URL params > cookie > defaults.
 */
export function getInitialViewState(): ViewState {
  const urlState = parseUrlParams();
  const cookieState = loadFromCookie();

  return {
    filters: urlState.filters || cookieState.filters || DEFAULT_VIEW_STATE.filters,
    sort: urlState.sort || cookieState.sort || DEFAULT_VIEW_STATE.sort,
    showDummy: urlState.showDummy !== undefined
      ? urlState.showDummy
      : cookieState.showDummy !== undefined
        ? cookieState.showDummy
        : DEFAULT_VIEW_STATE.showDummy,
    showFavoritesOnly: urlState.showFavoritesOnly !== undefined
      ? urlState.showFavoritesOnly
      : cookieState.showFavoritesOnly !== undefined
        ? cookieState.showFavoritesOnly
        : DEFAULT_VIEW_STATE.showFavoritesOnly,
  };
}

/**
 * Update browser URL with current view state (without page reload).
 */
export function updateUrl(state: ViewState): void {
  const queryString = buildUrlParams(state);
  const newUrl = window.location.pathname + queryString;
  window.history.replaceState(null, '', newUrl);
}

// ============================================
// Favorites Persistence (separate cookie)
// ============================================

/**
 * Load favorites from cookie.
 * Returns a Set of "ENV#APP" keys.
 */
export function loadFavorites(): Set<string> {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === FAVORITES_COOKIE_NAME && value) {
        const parsed = JSON.parse(decodeURIComponent(value));
        if (Array.isArray(parsed)) {
          return new Set(parsed);
        }
      }
    }
  } catch {
    // Invalid cookie, ignore
  }
  return new Set();
}

/**
 * Save favorites to cookie.
 * Expects a Set of "ENV#APP" keys.
 */
export function saveFavorites(favorites: Set<string>): void {
  const data = Array.from(favorites);

  const expires = new Date();
  expires.setDate(expires.getDate() + COOKIE_EXPIRY_DAYS);

  document.cookie = `${FAVORITES_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(data))};expires=${expires.toUTCString()};path=/`;
}
